import React, { useEffect, useState } from "react";
import { Input } from "antd";

import { readCalendlyMeetingUrl } from "@fitness-app/client-shared/features/consultations/upcoming-consultation";
import { type CalendarEvent } from "@fitness-app/data-models/entities/CalendarEvent";

import { analyticsService } from "~/store/initializeStore";
import { useUpdateCalendarEvent } from "../hooks/useUpdateCalendarEventStatus";

const HTTPS_URL = /^https:\/\/\S+$/i;

interface MeetingUrlFieldProps {
  event: Pick<CalendarEvent, "id" | "traineeId" | "meetingUrl" | "eventData">;
  source: "scheduled_events" | "trainee_calendar";
  width?: number | string;
}

/**
 * TRE-2214 — the trainer's video link for a consultation. Set, the client sees "Dołącz do spotkania"
 * instead of "Zadzwonimy na Twój numer telefonu"; cleared, she is back to the phone call - or to the
 * Zoom link Calendly attached, which the client gets without anyone typing it. That link is the
 * placeholder, so the trainer sees there is nothing to paste. Saved on
 * Enter or blur, and only when it is an https link: the client app opens this string as-is, and the
 * database refuses anything else anyway.
 */
export const MeetingUrlField = ({ event, source, width = 240 }: MeetingUrlFieldProps): React.ReactElement => {
  const saved = event.meetingUrl ?? "";
  const calendlyUrl = readCalendlyMeetingUrl(event.eventData);
  const [value, setValue] = useState(saved);
  const {
    mutation: { mutate, isLoading },
  } = useUpdateCalendarEvent();

  useEffect(() => setValue(saved), [saved]);

  const isInvalidUrl = (candidate: string) => candidate.length > 0 && !HTTPS_URL.test(candidate);
  const isInvalid = isInvalidUrl(value.trim());

  const save = (next: string) => {
    const trimmed = next.trim();

    if (trimmed === saved || isInvalidUrl(trimmed)) {
      return;
    }

    const meetingUrl = trimmed || null;

    mutate(
      { eventId: event.id, update: { meetingUrl, traineeId: event.traineeId || undefined } },
      {
        onSuccess: () =>
          analyticsService.track("consultation_meeting_link_set", {
            eventId: event.id,
            traineeId: event.traineeId,
            cleared: meetingUrl === null,
            source,
          }),
      },
    );
  };

  return (
    <Input
      value={value}
      placeholder={calendlyUrl ?? "https://"}
      title={calendlyUrl && !value ? "Link z Calendly" : undefined}
      allowClear
      disabled={isLoading}
      status={isInvalid ? "error" : undefined}
      style={{ width }}
      onChange={(e) => {
        setValue(e.target.value);
        // The clear icon fires a click-typed change and no blur, so it saves on its own.
        if (e.type === "click") {
          save("");
        }
      }}
      onBlur={() => save(value)}
      onPressEnter={() => save(value)}
    />
  );
};
